import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { logoUrl } from "@/components/LogoUploader";
import { PthrmLogo } from "@/components/PthrmLogo";
import {
  LayoutDashboard, Building2, Globe, Palette, Users, MapPin, CalendarDays, CalendarHeart, Wallet, Receipt, FileText, Briefcase, Rocket,
  Target, MessageSquareHeart, LifeBuoy, LogOut, DoorOpen, Timer, Bot, BarChart3, UsersRound, Menu, UserCircle, ShieldCheck, CalendarCog, Scale, CalendarRange, Network,
} from "lucide-react";

const ALL = ["super_admin", "admin", "manager", "employee"];
const NAV = [
  { group: "Platform", items: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ALL },
    { to: "/companies", label: "Companies", icon: Building2, roles: ["super_admin"] },
    { to: "/domain-settings", label: "Domain Settings", icon: Globe, roles: ["super_admin"] },
    { to: "/branding", label: "Branding", icon: Palette, roles: ["admin"] },
  ] },
  { group: "Core HR", items: [
    { to: "/employees", label: "Employees", icon: Users, roles: ["super_admin", "admin"] },
    { to: "/profile", label: "My Profile", icon: UserCircle, roles: ["manager", "employee"] },
    { to: "/team", label: "My Team", icon: UsersRound, roles: ["manager"] },
    { to: "/org-chart", label: "Org Chart", icon: Network, roles: ["admin", "manager"] },
    { to: "/attendance", label: "Attendance", icon: MapPin, roles: ["admin", "manager", "employee"] },
    { to: "/leaves", label: "Leaves", icon: CalendarDays, roles: ["admin", "manager", "employee"] },
    { to: "/leave-calendar", label: "Leave Calendar", icon: CalendarRange, roles: ["admin", "manager"] },
    { to: "/leave-policy", label: "Leave Policy", icon: CalendarCog, roles: ["admin"] },
    { to: "/leave-balances", label: "Leave Balances", icon: Scale, roles: ["admin"] },
    { to: "/holidays", label: "Holidays", icon: CalendarHeart, roles: ["admin", "manager", "employee"] },
    { to: "/timesheet", label: "Timesheet", icon: Timer, roles: ["admin", "manager", "employee"] },
  ] },
  { group: "Payroll", items: [
    { to: "/payroll", label: "Payroll Admin", icon: Wallet, roles: ["admin"] },
    { to: "/my-payroll", label: "My Payroll", icon: Receipt, roles: ["admin", "manager", "employee"] },
    { to: "/documents", label: "Documents", icon: FileText, roles: ["admin", "manager", "employee"] },
  ] },
  { group: "Talent", items: [
    { to: "/recruitment", label: "Recruitment", icon: Briefcase, roles: ["admin"] },
    { to: "/onboarding", label: "Onboarding", icon: Rocket, roles: ["admin", "manager", "employee"] },
    { to: "/performance", label: "Performance", icon: Target, roles: ["admin", "manager", "employee"] },
    { to: "/offboarding", label: "Offboarding", icon: DoorOpen, roles: ["admin", "manager", "employee"] },
  ] },
  { group: "Security", items: [
    { to: "/audit-log", label: "Audit Log", icon: ShieldCheck, roles: ["super_admin", "admin"] },
  ] },
  { group: "Engage", items: [
    { to: "/engagement", label: "Engagement", icon: MessageSquareHeart, roles: ["admin", "manager", "employee"] },
    { to: "/helpdesk", label: "Helpdesk", icon: LifeBuoy, roles: ["admin", "manager", "employee"] },
    { to: "/assistant", label: "AI Assistant", icon: Bot, roles: ["admin", "manager", "employee"] },
    { to: "/reports", label: "Reports & Alerts", icon: BarChart3, roles: ["super_admin", "admin", "manager"] },
  ] },
];

const ROLE_LABEL = { super_admin: "Super Admin", admin: "HR Admin", manager: "Manager", employee: "Employee" };

function SidebarNav({ role, onNavigate }) {
  return (
    <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
      {NAV.map((g) => {
        const items = g.items.filter((i) => i.roles.includes(role));
        if (!items.length) return null;
        return (
          <div key={g.group}>
            <div className="px-3 pb-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">{g.group}</div>
            {items.map((i) => (
              <NavLink key={i.to} to={i.to} end={i.to === "/"} onClick={onNavigate} data-testid={`nav-${i.to.replace("/", "") || "dashboard"}`}
                className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-[13px] font-medium transition-colors ${isActive ? "bg-blue-600 text-white" : "text-slate-400 hover:bg-slate-800/60 hover:text-white"}`}>
                <i.icon className="h-4 w-4 shrink-0" />{i.label}
              </NavLink>
            ))}
          </div>
        );
      })}
    </nav>
  );
}

function Brand({ user }) {
  return (
    <div className="border-b border-slate-800 px-5 py-4">
      <div className="flex items-center gap-2.5 truncate rounded-md border border-slate-800 bg-slate-800/50 px-2.5 py-1.5 text-xs text-slate-300" data-testid="sidebar-company">
        {logoUrl(user.company)
          ? <img src={logoUrl(user.company)} alt={user.company?.name} className="h-8 w-auto min-w-[2rem] max-w-[96px] shrink-0 rounded-md bg-white object-contain p-0.5" data-testid="sidebar-company-logo" />
          : <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-blue-600/20 text-[10px] font-extrabold text-blue-300" data-testid="sidebar-company-logo">{(user.company?.name || "PT").slice(0, 2).toUpperCase()}</div>}
        <span className="truncate">{user.company?.name || "All Companies"}</span>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const digi = /digivate/i.test(user.company?.name || "");
  useEffect(() => {
    if (digi && user.company?.code) localStorage.setItem("portal_code", user.company.code);
    else localStorage.removeItem("portal_code");
  }, [digi, user.company?.code]);
  const side = (close) => (
    <div className={`flex h-full flex-col bg-slate-900 ${digi ? "digivate-side" : ""}`} data-testid="app-sidebar">
      <Brand user={user} />
      <SidebarNav role={user.role} onNavigate={close} />
      <button onClick={async () => { await logout(); nav(digi && user.company?.code ? `/login/${user.company.code}` : "/login"); }} data-testid="logout-button" className="flex items-center gap-3 border-t border-slate-800 px-6 py-4 text-sm text-slate-400 hover:text-white">
        <LogOut className="h-4 w-4" /> Sign out
      </button>
    </div>
  );
  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="no-print sticky top-0 hidden h-screen w-64 shrink-0 lg:block">{side()}</aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 border-0 p-0"><SheetTitle className="sr-only">Menu</SheetTitle>{side(() => setOpen(false))}</SheetContent>
      </Sheet>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className={`no-print sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white/95 px-4 backdrop-blur-md sm:px-6 ${digi ? "digivate-top" : ""}`} data-testid="app-topbar">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button>
            <button onClick={() => nav("/")} className="flex items-center gap-2 rounded-md px-1 py-1 transition-opacity duration-200 hover:opacity-80" data-testid="topbar-pthrm-logo-link">
              <PthrmLogo className="h-8 w-8 rounded-md" testid="topbar-pthrm-logo" />
              <span className="font-display text-base font-extrabold tracking-tight text-slate-900">PTHRM</span>
            </button>
            <span className="hidden text-sm text-slate-500 sm:block">{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
          </div>
          <div className="flex items-center gap-3">
            {user.role !== "super_admin" && (
              <Button variant="outline" size="sm" onClick={() => nav("/assistant")} data-testid="topbar-ai-button" className="hidden sm:inline-flex"><Bot className="mr-1.5 h-4 w-4" />Ask AI</Button>
            )}
            <button onClick={() => nav("/profile")} className="flex items-center gap-2.5 rounded-md px-2 py-1 hover:bg-slate-100" data-testid="topbar-profile-button">
              <div className="hidden text-right leading-tight sm:block">
                <div className="text-sm font-semibold text-slate-900" data-testid="topbar-user-name">{user.name}</div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-blue-600" data-testid="topbar-user-role">{ROLE_LABEL[user.role]}</div>
              </div>
              <UserCircle className="h-8 w-8 text-slate-400" />
            </button>
          </div>
        </header>
        <main className="mx-auto w-full max-w-[1500px] flex-1 space-y-6 p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
